import { API_CONFIG, HTTP_STATUS } from '../config/api.config';
import { notifyAndRedirectToLogin } from './authRedirect';

/**
 * API Client utility for making HTTP requests
 */
class ApiClient {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  /**
   * Get authentication token from localStorage
   */
  getToken() {
    return localStorage.getItem('access_token');
  }

  /**
   * Set authentication token in localStorage
   */
  setToken(token) {
    localStorage.setItem('access_token', token);
  }

  /**
   * Remove authentication token from localStorage
   */
  removeToken() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  /**
   * Get default headers for requests
   */
  getHeaders(isFormData = false) {
    const headers = {};
    
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  resolveUrl(endpoint) {
    if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
      return endpoint;
    }

    return `${this.baseURL}${endpoint}`;
  }

  async request(endpoint, options = {}, retryOnUnauthorized = true) {
    const { headers = {}, isFormData = false, ...fetchOptions } = options;

    const response = await fetch(this.resolveUrl(endpoint), {
      ...fetchOptions,
      headers: {
        ...this.getHeaders(isFormData),
        ...headers,
      },
    });

    if (
      response.status === HTTP_STATUS.UNAUTHORIZED &&
      retryOnUnauthorized
    ) {
      const refreshed = await this.refreshToken();

      if (refreshed) {
        return this.request(endpoint, options, false);
      }

      notifyAndRedirectToLogin({
        pathname: window.location.pathname,
        message: 'Your session has expired. Please log in again.',
      });
      throw new Error('Session expired. Please login again.');
    }

    return response;
  }

  /**
   * Handle API response
   */
  async handleResponse(response) {
    const contentType = response.headers.get('content-type');
    
    // Handle blob responses (file downloads)
    if (contentType && contentType.includes('application/octet-stream')) {
      return response.blob();
    }

    const rawText = await response.text();

    let data;
    if (!rawText) {
      data = { 
        status: response.ok ? 'success' : 'error',
        message: response.ok ? 'Empty response from server' : 'Server returned an empty error response',
        detail: `Server returned ${response.status} ${response.statusText}`
      };
    } else {
      try {
        data = JSON.parse(rawText);
      } catch (error) {
        console.error('JSON parse error:', error);
        console.error('Response status:', response.status);
        console.error('Response statusText:', response.statusText);
        console.error('Response content type:', contentType);
        console.error('Raw response:', rawText.slice(0, 500));
        data = { 
          status: 'error',
          message: 'Invalid response format from server',
          detail: rawText.slice(0, 500)
        };
      }
    }

    if (!response.ok) {
      // For validation errors (400), return the error response structure
      // This allows components to access detailed error messages
      if (response.status === HTTP_STATUS.BAD_REQUEST && data.status === 'error') {
        return data; // Return error response as-is for proper handling
      }

      // For other errors, throw
      const error = new Error(data.message || 'An error occurred');
      error.status = response.status;
      error.data = data;
      throw error;
    }

    if (data?.status === 'error' && data?.message === 'Invalid response format from server') {
      const error = new Error(data.message);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  /**
   * Refresh authentication token
   */
  async refreshToken() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseURL}/auth/token/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        this.setToken(data.access);
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }

    return false;
  }

  /**
   * Make GET request
   */
  async get(endpoint, params = {}) {
    const url = new URL(`${this.baseURL}${endpoint}`);
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value === undefined || value === null || value === '') {
        return;
      }
      url.searchParams.append(key, value);
    });

    const response = await this.request(url.toString(), {
      method: 'GET',
    });

    return this.handleResponse(response);
  }

  /**
   * Make POST request
   */
  async post(endpoint, data = {}, isFormData = false, options = {}) {
    const response = await this.request(endpoint, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      isFormData,
      ...options,
    });

    return this.handleResponse(response);
  }

  /**
   * Make PUT request
   */
  async put(endpoint, data = {}, isFormData = false) {
    const response = await this.request(endpoint, {
      method: 'PUT',
      body: isFormData ? data : JSON.stringify(data),
      isFormData,
    });

    return this.handleResponse(response);
  }

  /**
   * Make PATCH request
   */
  async patch(endpoint, data = {}, isFormData = false) {
    const response = await this.request(endpoint, {
      method: 'PATCH',
      body: isFormData ? data : JSON.stringify(data),
      isFormData,
    });

    return this.handleResponse(response);
  }

  /**
   * Make DELETE request
   */
  async delete(endpoint) {
    const response = await this.request(endpoint, {
      method: 'DELETE',
    });

    return this.handleResponse(response);
  }

  /**
   * Download file
   */
  async downloadFile(endpoint, filename) {
    const response = await this.request(endpoint, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Download failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

const apiClient = new ApiClient();

export default apiClient;
